import { useState } from "react";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionTag, Magnetic } from "./Shared";

const details = [
  { label: "Email", value: "info@adi-semaradana.com", href: "mailto:info@adi-semaradana.com", testId: "contact-email-link" },
  { label: "Phone", value: "+1 888 557 6994", href: "tel:+18885576994", testId: "contact-phone-link" },
];

const inputCls =
  "w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-4 text-base font-light placeholder:text-black/35 focus:outline-none focus:border-[#0A0A0A] focus:ring-0 transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", message: "" });

  const onSubmit = (e) => {
    e.preventDefault();
    toast.success("Thank you — we reply within one business day.");
    setForm({ name: "", email: "", phone: "", interest: "", message: "" });
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section id="contact" data-testid="contact-section" className="border-b border-black/15 bg-[#EBE9E1]">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 py-24 md:py-40 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-6">
          <SectionTag testId="contact-tag">Begin a conversation</SectionTag>
          <Reveal delay={0.1}>
            <h2 className="mt-10 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              A calmer, better-performing <em className="font-light">portfolio.</em>
            </h2>
            <p className="mt-8 max-w-md text-base lg:text-lg leading-relaxed text-black/60 font-light">
              Send a short note and we will respond within one business day. Every conversation is confidential,
              and the introductory call carries no fee.
            </p>
          </Reveal>

          <div className="mt-14 space-y-0 border-t border-black/15 max-w-md">
            {details.map((d, i) => (
              <Reveal key={d.label} delay={0.15 + i * 0.08}>
                <a href={d.href} data-testid={d.testId} className="group flex items-baseline justify-between py-6 border-b border-black/15">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-black/45">{d.label}</span>
                  <span className="font-serif text-2xl md:text-3xl tracking-tight group-hover:text-[#D4512D] transition-colors duration-300">
                    {d.value}
                  </span>
                </a>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="flex items-baseline justify-between py-6 border-b border-black/15">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-black/45">Registered Office</span>
                <span className="text-sm text-black/70 font-light text-right">
                  30 N Gould St Ste R
                  <br />
                  Sheridan, WY 82801, USA
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.35}>
              <div className="flex items-baseline justify-between py-6 border-b border-black/15">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-black/45">Working Focus</span>
                <span className="text-sm text-black/70 font-light text-right">London W1D · Prime UK postcodes</span>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.2} className="lg:col-span-6">
          <form data-testid="contact-form" onSubmit={onSubmit} className="lg:pl-16 space-y-10">
            <div className="grid sm:grid-cols-2 gap-10">
              <div>
                <label htmlFor="contact-name" className="font-mono text-[10px] uppercase tracking-[0.3em] text-black/45">
                  Your name *
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Full name"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="font-mono text-[10px] uppercase tracking-[0.3em] text-black/45">
                  Email *
                </label>
                <input
                  id="contact-email"
                  type="email"
                  data-testid="contact-email-input"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@company.com"
                  className={inputCls}
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-10">
              <div>
                <label htmlFor="contact-phone" className="font-mono text-[10px] uppercase tracking-[0.3em] text-black/45">
                  Phone (optional)
                </label>
                <input
                  id="contact-phone"
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+65 ..."
                  className={inputCls}
                />
              </div>
              <div className="relative">
                <label htmlFor="contact-interest" className="font-mono text-[10px] uppercase tracking-[0.3em] text-black/45">
                  Area of interest
                </label>
                <select
                  id="contact-interest"
                  data-testid="contact-interest-select"
                  value={form.interest}
                  onChange={set("interest")}
                  className={`${inputCls} appearance-none pr-8 ${form.interest ? "" : "text-black/35"}`}
                >
                  <option value="" disabled>
                    Select an option
                  </option>
                  <option>Property Advisory — Yield Blueprint</option>
                  <option>Refurbishment &amp; Vendor Oversight</option>
                  <option>Operator Selection &amp; Stewardship</option>
                  <option>Full Written Mandate</option>
                  <option>Something else</option>
                </select>
                <ChevronDown size={16} strokeWidth={1.5} className="absolute right-0 bottom-5 text-black/40 pointer-events-none" />
              </div>
            </div>
            <div>
              <label htmlFor="contact-message" className="font-mono text-[10px] uppercase tracking-[0.3em] text-black/45">
                Brief message
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="A few lines on your situation and objectives…"
                className={`${inputCls} resize-none`}
              />
            </div>
            <p className="text-xs text-black/45 font-light leading-relaxed">
              By submitting, you agree to be contacted regarding your enquiry. We never share your details with
              third parties.
            </p>
            <Magnetic>
              <button
                type="submit"
                data-testid="contact-submit-button"
                className="inline-flex items-center gap-4 rounded-full bg-[#0A0A0A] text-[#F4F3EF] px-10 py-5 font-mono text-xs uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-[#D4512D]"
              >
                Request Consultation <ArrowUpRight size={16} strokeWidth={1.5} />
              </button>
            </Magnetic>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
